/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import styles from './promo.module.css';
import { useStores } from '../../hooks/useStore';
import { observer } from 'mobx-react';

import {
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
} from '@mui/material';
import { Button } from '../../components/UI/Button/Button';
import { ReactComponent as EditIco } from '../events/icon/edit.svg';
import { AppSettings } from '../../appSettings';
import { EventsModel } from '../../models/events';
import { PromoCourse } from '../../models/promoCourses';
import EventsPopUP from '../../components/popUp/Events/Events';
import CoursesPopUP from '../../components/popUp/Courses/Courses';

type PromoTab = 0 | 1;

const Promo: React.FC = observer(() => {
  const api = useApi();
  const store = useStores();

  const [activeTab, setActiveTab] = useState<PromoTab>(0);

  // ── Мероприятия ──────────────────────────────────────────────
  const [eventsPage, setEventsPage] = useState(0);
  const [eventsRowsPerPage, setEventsRowsPerPage] = useState(25);
  const [events, setEvents] = useState<EventsModel[]>([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventsModel | null>(null);

  // ── Рекламные курсы ───────────────────────────────────────────
  const [coursesPage, setCoursesPage] = useState(0);
  const [coursesRowsPerPage, setCoursesRowsPerPage] = useState(25);
  const [courses, setCourses] = useState<PromoCourse[]>([]);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState<PromoCourse | null>(null);

  useEffect(() => {
    loadEvents();
  }, [eventsPage, eventsRowsPerPage]);

  useEffect(() => {
    loadCourses();
  }, [coursesPage, coursesRowsPerPage]);

  const loadEvents = async () => {
    try {
      store.notificationsStore.showPreloader();
      const { body } = await api.events.get(
        eventsPage * eventsRowsPerPage,
        eventsRowsPerPage,
      );
      if (body) {
        setEvents(body);
      }
    } catch {
      store.notificationsStore.setError('Ошибка при загрузке мероприятий');
    } finally {
      store.notificationsStore.hidePreloader();
    }
  };

  const loadCourses = async () => {
    try {
      store.notificationsStore.showPreloader();
      const { body } = await api.promoCourses.get(
        coursesPage * coursesRowsPerPage,
        coursesRowsPerPage,
      );
      if (body) {
        setCourses(body as PromoCourse[]);
      }
    } catch {
      store.notificationsStore.setError('Ошибка при загрузке курсов');
    } finally {
      store.notificationsStore.hidePreloader();
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, value: PromoTab) => {
    setActiveTab(value);
  };

  // ── Events handlers ───────────────────────────────────────────
  const closeEventForm = () => {
    setShowEventForm(false);
    setEditingEvent(null);
  };

  const openEditEvent = (item: EventsModel) => {
    setEditingEvent(item);
    setShowEventForm(true);
  };

  // ── Courses handlers ──────────────────────────────────────────
  const closeCourseForm = () => {
    setShowCourseForm(false);
    setEditingCourse(null);
  };

  const openEditCourse = (item: PromoCourse) => {
    setEditingCourse(item);
    setShowCourseForm(true);
  };

  return (
    <>
      {showEventForm && (
        <EventsPopUP
          onClose={closeEventForm}
          update={loadEvents}
          item={editingEvent}
        />
      )}
      {showCourseForm && (
        <CoursesPopUP
          onClose={closeCourseForm}
          update={loadCourses}
          item={editingCourse}
          variant="promo"
        />
      )}

      <div className={styles.container}>
        <div className={styles.header}>
          <h2>Реклама в приложении</h2>
        </div>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          className={styles.tabs}
        >
          <Tab label="Мероприятия" />
          <Tab label="Курсы (реклама)" />
        </Tabs>

        {/* ── Вкладка: Мероприятия ── */}
        {activeTab === 0 && (
          <div className={styles.tabContent}>
            <div className={styles.top}>
              <Button
                style={{ maxWidth: 150 }}
                appearance="auth"
                onClick={() => setShowEventForm(true)}
              >
                Добавить
              </Button>
            </div>

            <Paper
              sx={{
                width: '100%',
                maxWidth: 1500,
                margin: '5px',
                overflowX: 'auto',
              }}
            >
              <TableContainer style={{ marginTop: 20, overflowX: 'auto' }}>
                <Table sx={{ minWidth: 320 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Заголовок</TableCell>
                      <TableCell align="right">Ссылка</TableCell>
                      <TableCell align="right">Изображение</TableCell>
                      <TableCell align="right">Действие</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {row.title}
                        </TableCell>
                        <TableCell align="right">{row.url}</TableCell>
                        <TableCell align="right">
                          <img
                            src={AppSettings.api.url + row.image}
                            width={200}
                            alt={row.title}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <EditIco
                            className={styles.editIco}
                            onClick={() => openEditEvent(row)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <div
                style={{ width: '100%', height: 1, backgroundColor: '#E0E0E0' }}
              />
              <TablePagination
                labelRowsPerPage="Строк на странице"
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={events.length}
                rowsPerPage={eventsRowsPerPage}
                page={eventsPage}
                onPageChange={(_, p) => setEventsPage(p)}
                onRowsPerPageChange={(e) => {
                  setEventsRowsPerPage(+e.target.value);
                  setEventsPage(0);
                }}
              />
            </Paper>
          </div>
        )}

        {/* ── Вкладка: Курсы (реклама) ── */}
        {activeTab === 1 && (
          <div className={styles.tabContent}>
            <div className={styles.top}>
              <Button
                style={{ maxWidth: 150 }}
                appearance="auth"
                onClick={() => setShowCourseForm(true)}
              >
                Добавить
              </Button>
            </div>

            <Paper
              sx={{
                width: '100%',
                maxWidth: 1500,
                margin: '5px',
                overflowX: 'auto',
              }}
            >
              <TableContainer style={{ marginTop: 20, overflowX: 'auto' }}>
                <Table sx={{ minWidth: 320 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Заголовок</TableCell>
                      <TableCell align="right">Описание</TableCell>
                      <TableCell align="right">Ссылка</TableCell>
                      <TableCell align="right">Изображение</TableCell>
                      <TableCell align="right">Действие</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {courses.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {row.title}
                        </TableCell>
                        <TableCell align="right">{row.description}</TableCell>
                        <TableCell align="right">{row.url}</TableCell>
                        <TableCell align="right">
                          <img
                            src={AppSettings.api.url + row.image}
                            width={200}
                            alt={row.title}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <EditIco
                            className={styles.editIco}
                            onClick={() => openEditCourse(row)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <div
                style={{ width: '100%', height: 1, backgroundColor: '#E0E0E0' }}
              />
              <TablePagination
                labelRowsPerPage="Строк на странице"
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={courses.length}
                rowsPerPage={coursesRowsPerPage}
                page={coursesPage}
                onPageChange={(_, p) => setCoursesPage(p)}
                onRowsPerPageChange={(e) => {
                  setCoursesRowsPerPage(+e.target.value);
                  setCoursesPage(0);
                }}
              />
            </Paper>
          </div>
        )}
      </div>
    </>
  );
});

export default Promo;
